import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import FilesCard from "./files-card";
import Input from "models/input/input";
import Metadata from "./metadata-card/metadata/Metadata";
import { useEffect, useMemo, useState } from "react";
import SolrService from "services/SolrService";
import { useParams } from "react-router-dom";
import Boundary from "models/geospatial/boundary";
import Point from "models/geospatial/point";
import Asset from "datalign-module-template/models/asset";
import MetadataCard from "./metadata-card/MetadataCard";
import ProjectCard from "./project-card";
import Template from "models/template";
import Header from "../header/Header";
import AnalyticsService from "services/AnalyticsService";

const analyticsService = new AnalyticsService();

const { solrService, inputService } = new SolrService();

export default function InputPage() {
  const { id } = useParams();

  const [input, setInput] = useState<Input | null>();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [boundaries, setBoundaries] = useState<Boundary[]>([]);
  const [points, setPoints] = useState<Point[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);

  useEffect(() => {
    if (id)
      solrService.get<Input>(id).then((res) => {
        if (res) setInput(res);
        else setInput(null);
      });
  }, [id]);

  useEffect(() => {
    if (input) {
      const getRequests = [];
      getRequests.push(
        inputService
          .templates(input.input_id)
          .then((res) => res && setTemplates(res.data)),
      );
      getRequests.push(
        inputService.boundaries(input.input_id).then((res) => {
          return res && setBoundaries(res.data);
        }),
      );
      getRequests.push(
        inputService.points(input.input_id).then((res) => {
          res && setPoints(res.data);
        }),
      );
      getRequests.push(
        inputService
          .assets(input.input_id)
          .then((res) => res && setAssets(res.data)),
      );
    }
  }, [input]);

  useEffect(() => {
    analyticsService.send();
  }, []);

  const dataAssets = useMemo(
    () => assets.filter((asset) => asset.data_or_attachment === "Data"),
    [assets],
  );
  const attachmentsAssets = useMemo(
    () => assets.filter((asset) => asset.data_or_attachment === "Attachments"),
    [assets],
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          mb: 5,
          width: { xl: "80vw", md: "90vw", sm: "95vw" },
        }}
      >
        <Header showBreadcrumb={true} input={input} />
        {!input ? (
          <Box
            sx={{
              width: "100%",
              maxWidth: "95vw",
            }}
          >
            <Box
              sx={{
                py: "32px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                overflowX: "hidden",
                position: "relative",
                width: "100%",
                height: "100%",
              }}
            >
              {input === undefined ? (
                <CircularProgress />
              ) : (
                <Typography
                  variant="h2"
                  fontSize="45px"
                  lineHeight="52px"
                  fontWeight={400}
                >
                  Input not found!
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              maxWidth: "95vw",
            }}
          >
            <Box py={2}>
              <Typography
                justifyContent="start"
                py={3}
                variant="h2"
                sx={{
                  ...(["FAF-nt", "nrdata-orig"].includes(
                    process.env.REACT_APP_STYLING_THEME ?? "",
                  )
                    ? {
                        justifyContent: "start",
                        fontSize: "2.0325rem",
                        fontFamily: "inherit",
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: "#50505a",
                      }
                    : {}),
                }}
              >
                {input.title}
              </Typography>
              <Metadata
                input={input}
                boundaries={boundaries}
                points={points}
                templates={[]}
                placement="header"
              />
            </Box>
            <Box py={2}>
              <Grid container spacing={2} direction="row-reverse">
                <Grid size={{ sm: 12, md: 8 }}>
                  <MetadataCard
                    input={input}
                    boundaries={boundaries}
                    points={points}
                    templates={templates}
                    placement="metadata"
                  />
                </Grid>
                <Grid size={{ sm: 12, md: 4 }} width="100%">
                  <ProjectCard
                    input={input}
                    boundaries={boundaries}
                    points={points}
                    placement="project"
                  />
                </Grid>
              </Grid>
              {dataAssets.length > 0 && (
                <FilesCard type="Data" assets={dataAssets} />
              )}
              {attachmentsAssets.length > 0 && (
                <FilesCard type="Attachments" assets={attachmentsAssets} />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
